const CONVERTIBLE_IMAGE_TYPES = new Set(['image/jpeg', 'image/png']);
const WEBP_MIME_TYPE = 'image/webp';

function webpFileName(originalName) {
  const cleanName = String(originalName || 'image').replace(/\.[^/.]+$/, '') || 'image';
  return `${cleanName}.webp`;
}

function calculateTargetSize(width, height, maxDimension) {
  if (!width || !height) throw new Error('Image dimensions read thai shakya nahi.');

  const longestSide = Math.max(width, height);
  if (longestSide <= maxDimension) return { width, height };

  const scale = maxDimension / longestSide;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

async function decodeWithImageBitmap(file) {
  if (typeof createImageBitmap !== 'function') return null;

  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    return {
      source: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      release: () => bitmap.close?.(),
    };
  } catch {
    try {
      const bitmap = await createImageBitmap(file);
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        release: () => bitmap.close?.(),
      };
    } catch {
      return null;
    }
  }
}

function decodeWithImageElement(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => resolve({
      source: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      release: () => URL.revokeObjectURL(objectUrl),
    });
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Image browser ma decode thai shaki nahi.'));
    };
    image.src = objectUrl;
  });
}

function canvasToWebp(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob || blob.type !== WEBP_MIME_TYPE) {
        reject(new Error('Aa browser WebP conversion support karto nathi.'));
        return;
      }
      resolve(blob);
    }, WEBP_MIME_TYPE, quality);
  });
}

export async function optimizeImageForUpload(file, {
  maxDimension = 1600,
  quality = 0.82,
} = {}) {
  const sourceType = String(file?.type || '').toLowerCase();
  const originalBytes = Number(file?.size || 0);

  if (!CONVERTIBLE_IMAGE_TYPES.has(sourceType)) {
    return {
      file,
      converted: false,
      originalBytes,
      uploadedBytes: originalBytes,
      reason: sourceType === WEBP_MIME_TYPE ? 'already-webp' : 'unsupported-source-format',
    };
  }

  let decoded;
  try {
    decoded = await decodeWithImageBitmap(file) || await decodeWithImageElement(file);
    const target = calculateTargetSize(decoded.width, decoded.height, maxDimension);
    const canvas = document.createElement('canvas');
    canvas.width = target.width;
    canvas.height = target.height;

    const context = canvas.getContext('2d', { alpha: true });
    if (!context) throw new Error('Image conversion canvas available nathi.');

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(decoded.source, 0, 0, target.width, target.height);

    const blob = await canvasToWebp(canvas, quality);
    const optimizedFile = new File([blob], webpFileName(file.name), {
      type: WEBP_MIME_TYPE,
      lastModified: file.lastModified || Date.now(),
    });

    canvas.width = 1;
    canvas.height = 1;

    return {
      file: optimizedFile,
      converted: true,
      originalBytes,
      uploadedBytes: optimizedFile.size,
      width: target.width,
      height: target.height,
      sourceWidth: decoded.width,
      sourceHeight: decoded.height,
    };
  } catch (error) {
    return {
      file,
      converted: false,
      originalBytes,
      uploadedBytes: originalBytes,
      reason: 'conversion-fallback',
      conversionError: error instanceof Error ? error.message : String(error),
    };
  } finally {
    decoded?.release?.();
  }
}
